import ApiService from './api';

/** A channel entry as it appears on a bot document. */
export interface BotChannel {
  /** Type of the channel (e.g. 'facebook', 'widget') */
  channel: string;
  /** Unique ID of this channel */
  channelId: string;
  /** Whether the channel is currently active */
  active?: boolean;
  /** Human-friendly name shown in the UI */
  displayName?: string;
  /** Context data for the channel */
  context?: any;
  /** Additional payload data for the channel */
  payload?: any;
  /** Whether the channel supports multiple languages */
  multiLanguage?: boolean;
  /** Extra channel fields the API may return that we don't model here */
  [key: string]: any;
}

/** Fields used by hasActiveChannel/linkToChannel; a full bot document has many more. */
export interface BotLike {
  /** Channels configured on the bot */
  channels?: BotChannel[];
  /** ID of the staging twin of this bot, if any */
  stagingBot?: string;
  /** Public unique ID of the bot */
  uniqueBotId?: string;
  /** Extra bot fields not modeled here */
  [key: string]: any;
}

/** A custom regular-expression entity configured for NLP parameter extraction. */
export interface RegexpEntity {
  /** Discriminator — always 'regexp' for these entities */
  type: 'regexp';
  /** Human-friendly name shown in the UI */
  displayName: string;
  /** The regular expression source to match against user input */
  expression: string;
  /** Name of the context parameter the match is stored into */
  parameter: string;
}

const BotService = {
  get: async function (): Promise<any> {
    try {
      const response = await ApiService.get('/bot');
      return response.data;
    } catch (error) {
      return [];
    }
  },
  getBot: async function (id: string): Promise<any> {
    try {
      const response = await ApiService.get(`/schaltzentrale/bot/${id}`);
      return response.data;
    } catch (error) {
      return [];
    }
  },
  /**
   * Updates the bot's name.
   * @param botId - The bot's unique ID.
   * @param name - The new name for the bot.
   * @returns Success envelope with updated bot data, or the error on failure.
   */
  async updateBotName(botId: string, name: string): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const data = await ApiService.put(`/schaltzentrale/bot/${botId}`, { name });
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  },
  hasActiveChannel: function (bot: BotLike | null | undefined, type: string): boolean {
    if (type === 'widget') return true;

    if (!bot || !Array.isArray(bot.channels)) return false;
    if (bot.channels.some((c) => c.channel === type && c.active)) {
      return true;
    }

    return false;
  },
  /** Builds a public test link for the given channel type; empty string when not applicable. */
  linkToChannel: function (bot: BotLike | null | undefined, type: string): string {
    if (!bot || !Array.isArray(bot.channels)) return '';
    const channel = bot.channels.find((c) => c.channel === type);

    if (type === 'facebook') {
      return 'https://m.me/' + channel!.channelId;
    }

    if (type === 'widget') {
      return (
        'https://preview.moin.ai/?bot=' + (bot.stagingBot || bot.uniqueBotId)
      );
    }

    return '';
  },
  /** Activates (active = true) or fully deactivates (active = false) a bot. */
  async setBotActive(botId: string, active: boolean)
  : Promise<{ active: boolean; deactivatedAt: string | null; channels: { channelId: string; active: boolean }[] }> {
    const response = await ApiService.put(`/schaltzentrale/bot/${botId}/active`, { active });
    return response?.data;
  },
  async saveSettings(botid: string, settings: any, settingType: string = ''): Promise<any> {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/bot/settings/${botid}/${settingType}`,
        settings
      );
      return response?.data;
    } catch (error: any) {
      throw new Error(error);
    }
  },
  async newBot({ name, language, packageName, fallbackFaqIntent }: {
    /** Display name for the new bot */
    name: string;
    /** Bot language code, e.g. 'de' */
    language: string;
    /** Licence package identifier (named packageName because `package` is a reserved word) */
    packageName: string;
    /** Whether the fallback FAQ intent should be active */
    fallbackFaqIntent: boolean;
  }): Promise<any> {
    try {
      const response = await ApiService.post(`/bot/`, { name, language, package: packageName, fallbackFaqIntent: { active: fallbackFaqIntent } });
      return response;
    } catch (error) {
      return false;
    }
  },
  async getAccounts(botId: string): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/bot/accounts/${botId}/`
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },
  async getBotInvites(botId: string): Promise<any> {
    try {
      const response = await ApiService.get(`/bot/botinvites/${botId}/`);
      return response.data;
    } catch (error) {
      return false;
    }
  },
  async addChannel(channel: any): Promise<any> {
    try {
      const response = await ApiService.post(`/bot/channel/`, channel);
      return response.data;
    } catch (error) {
      return false;
    }
  },

  /**
   * Updates a bot channel only with the allowed fields
   * @param params.botId - Bot ID
   * @param params.channel - Channel update data (must include channelId)
   * @returns Success envelope with the updated data, or an error string/object on failure.
   */
  async updateChannel({ botId, channel = {} }: {
    /** Bot ID (sent as uniqueBotId) */
    botId?: string;
    /** Channel update data; only whitelisted fields are forwarded */
    channel?: Partial<BotChannel>;
  } = {}): Promise<{ success: boolean; data?: any; error?: any }> {
    const { channelId, active, channel: channelType, context, payload, displayName, multiLanguage } = channel;
    if (!channelId || !botId) return { success: false, error: 'channelId and botId are required' };

    const body: Record<string, any> = { uniqueBotId: botId, channelId };
    if (typeof active !== 'undefined') body.active = active;
    if (typeof channelType !== 'undefined') body.channel = channelType;
    if (typeof context !== 'undefined') body.context = context;
    if (typeof payload !== 'undefined') body.payload = payload;
    if (typeof displayName !== 'undefined') body.displayName = displayName;
    if (typeof multiLanguage !== 'undefined') body.multiLanguage = multiLanguage;

    try {
      const response = await ApiService.post(`/bot/channel/update`, body);
      // Because of 400 interceptor for moin-api
      if (!response || !response.data || typeof response.data !== 'object') {
        const error = response.data || 'Something went wrong while updating the channel';
        return { success: false, error };
      }

      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: error?.response?.data || 'Failed to update channel' };
    }
  },
  // @data: {active: boolean, features: string[]}
  async updateUser(email: string, data: { active?: boolean; features?: string[] } & Record<string, any>): Promise<any> {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/bot/accounts/${email}`,
        data
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },

  async updateBlockedChannels(email: string, blockedChannels: string[], uniqueBotId: string): Promise<any> {
    try {
      const response = await ApiService.put(
        `/schaltzentrale/bot/accounts/${email}/blockedChannels`,
        { blockedChannels, uniqueBotId }
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },

  async deleteUser(email: string, botId: string): Promise<any> {
    try {
      const response = await ApiService.delete(
        `/schaltzentrale/bot/accounts/${email}`,
        { uniqueBotId: botId }
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },

  async getNlPFeatures(botId: string): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/nlp/${botId}/features`
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  async activateKeywords({ botIdLive, botIdStaging }: {
    /** ID of the live bot */
    botIdLive: string;
    /** ID of the staging bot */
    botIdStaging: string;
  }): Promise<boolean | {}> {
    try {
      const resLive = await ApiService.post(
        `/schaltzentrale/nlp/${botIdLive}/features/keywordmatch`
      );
      const resStaging = await ApiService.post(
        `/schaltzentrale/nlp/${botIdStaging}/features/keywordmatch`
      );

      if (resLive?.status !== 200 || resStaging?.status !== 200) return false;
      return true;
    } catch (error) {
      return {};
    }
  },
  async deactivateKeywords({ botIdLive, botIdStaging }: {
    /** ID of the live bot */
    botIdLive: string;
    /** ID of the staging bot */
    botIdStaging: string;
  }): Promise<boolean> {
    try {
      const resLive = await ApiService.delete(
        `/schaltzentrale/nlp/${botIdLive}/features/keywordmatch`
      );
      const resStaging = await ApiService.delete(
        `/schaltzentrale/nlp/${botIdStaging}/features/keywordmatch`
      );
      if (resLive?.status !== 200 || resStaging?.status !== 200) return false;
      return true;
    } catch (error) {
      return false;
    }
  },
  async set2FAActivation({ botId, activate, forSSO }: {
    /** ID of the bot */
    botId: string;
    /** true to enable two-factor auth, false to disable */
    activate: boolean;
    /** Whether the 2FA setting applies to SSO logins */
    forSSO?: boolean;
  }): Promise<any> {
    try {
      const response = await ApiService.post(`/bot/2FA/${botId}`, {
        activate,
        forSSO: !!forSSO,
      });

      return response?.data;
    } catch (error) {
      return false;
    }
  },
  async setSSOSettings({ botId, provider, sso2FA }: {
    /** ID of the bot */
    botId: string;
    /** SSO provider identifier (e.g. 'microsoft', 'google') */
    provider: string;
    /** Whether 2FA is enforced for SSO logins */
    sso2FA?: boolean;
  }): Promise<any> {
    try {
      const response = await ApiService.post(`/schaltzentrale/bot/settings/${botId}/sso`, {
        provider,
        sso2FA,
      });

      return response?.data;
    } catch (error) {
      return false;
    }
  },
  // @data example {
  //     "demoStepId": "640f2763baff1cdfdbccd146",
  //     "createAnswer": false,
  //     "selectTopics": [9,10,11,12],
  //     "advancedSamples": true
  // }
  async updateDemoBotIntents(botId: string, data: any): Promise<any> {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/intentbootstrap/fromdemostep/${botId}`,
        data
      );
      return response?.data;
    } catch (error) {
      return false;
    }
  },
  async setHighPrivacyActivation({ botId, activate }: {
    /** ID of the bot */
    botId: string;
    /** true to enable anonymization, false to disable */
    activate: boolean;
  }): Promise<any> {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/nlp/anonymization/${botId}/${
          activate ? 'enable' : 'disable'
        }`
      );

      return response?.data;
    } catch (error) {
      return false;
    }
  },
  /**
   * Runs an anonymization method on a text with the given (possibly unsaved) config, via the bot's internal endpoint.
   * Throws on failure so the caller can show why it failed (e.g. HTTP 429 when rate limited).
   * @returns The anonymized text.
   */
  async anonymiseTest({ botId, method, text, config, language = '', regexps = [] }: {
    botId: string;
    /** Anonymization method, e.g. 'dictionary' or 'moinAnon'. Empty when only RegExps are tested. */
    method?: string | null;
    text: string;
    /** That method's config, passed to the bot as is. */
    config: Record<string, any>;
    language?: string;
    /** RegExps `{ parameter, expression }` applied before the method. */
    regexps?: { parameter: string; expression: string }[];
  }): Promise<string> {
    const response = await ApiService.post(
      `/schaltzentrale/bot/${botId}/internal/playground?path=anonymise`,
      // regexps only when set, so method-only tests send exactly the request they sent before
      { text, method, config, language, ...(regexps.length ? { regexps } : {}) }
    );
    return response.data.data;
  },
  async searchBot(botId: string, query: string): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/bot/${botId}/search/${query}`
      );
      return response?.data;
    } catch (error) {
      return false;
    }
  },
  async getContentByAction(botId: string, actionName: string): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/bot/${botId}/actionname/${actionName}`
      );
      if (response?.data?.error) {
        console.error("Loading content error: ", response?.data?.error);
        return false;
      }
      return response?.data;
    } catch (error) {
      return false;
    }
  },
  async updateEditedAction(uniqueBotId: string, action: any): Promise<boolean> {
    try {
      const response = await ApiService.post(`/bot/actions/edit`, { action, uniqueBotId });
      return response?.status === 200;
    } catch (error) {
      return false;
    }
  },

  /**
   * Fetches all configured regexp entities for a bot.
   * @returns Array of regexp entities, or `false` on error.
   */
  async getAllRegEpx(botId: string): Promise<RegexpEntity[] | false> {
    try {
      const response = await ApiService.get(`/schaltzentrale/regexp/${botId}/all/`);
      return response.data;
    } catch (error) {
      return false;
    }
  },
  /**
   * Replaces the regexp entities of a bot.
   * @param botId ID of the bot
   * @param data The full list of regexp entities to store
   */
  async setRegExp(botId: string, data: any[]): Promise<
    { success: true; data: any[] } |
    { success: false; error: { code?: any; status?: any; message?: any } }
  > {
    try {
      const response = await ApiService.put(`/schaltzentrale/regexp/${botId}`, data);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error };
    }
  },

  /**
   * Creates a new API key for a bot
   *
   * @param uniqueBotId - ID of the bot
   * @returns Created API key data or error object
   */
  async createApiKey(uniqueBotId: string): Promise<any | { error: string }> {
    try {
      const response = await ApiService.post('/bot/apikeys', { uniqueBotId });
      return response.data.key;
    } catch (error: any) {
      console.error('[createApiKey] Error:', error?.message || error);
      return {
        error: error?.response?.data?.message || 'Failed to create API key'
      };
    }
  },

  /**
   * Updates an API key status (activate/deactivate)
   *
   * @param updateData - Data needed to update an API key
   * @returns Success confirmation or error object
   */
  async updateApiKeyStatus(updateData: {
    /** ID of the bot */
    botId: string;
    /** The API key to update */
    apiKey: string;
    /** New status for the API key */
    active: boolean;
  }): Promise<any | { error: string }> {
    try {
      const response = await ApiService.put('/bot/apikeys', updateData);
      return response.data;
    } catch (error: any) {
      console.error('[updateApiKeyStatus] Error:', error?.message || error);
      return {
        error: error?.response?.data?.message || 'Failed to update API key status'
      };
    }
  },

  /**
   * Soft-deletes an API key
   *
   * @param uniqueBotId - ID of the bot
   * @param key - The API key to delete
   * @returns Success confirmation or error object
   */
  async deleteApiKey(uniqueBotId: string, key: string): Promise<{ success: boolean } | { error: string }> {
    try {
      const response = await ApiService.delete('/bot/apikeys', { uniqueBotId, key });
      return { success: response.status === 200 };
    } catch (error: any) {
      console.error('[deleteApiKey] Error:', error?.message || error);
      return {
        error: error?.response?.data?.message || 'Failed to delete API key'
      };
    }
  },

  /**
   * Enables/disables MCP endpoint access for an API key
   *
   * @param uniqueBotId - ID of the bot
   * @param key - The API key to update
   * @param mcpEnabled - Whether the key may access the MCP endpoint
   * @param mcpScope - Access level granted over MCP ('read' | 'write')
   * @returns Success confirmation or error object
   */
  async updateApiKeyMcp(
    uniqueBotId: string,
    key: string,
    mcpEnabled: boolean,
    mcpScope?: 'read' | 'write'
  ): Promise<any | { error: string }> {
    try {
      const response = await ApiService.put('/bot/apikeys/mcp', { uniqueBotId, key, mcpEnabled, mcpScope });
      return response.data;
    } catch (error: any) {
      console.error('[updateApiKeyMcp] Error:', error?.message || error);
      return {
        error: error?.response?.data?.message || 'Failed to update MCP access'
      };
    }
  },

  /**
   * Gets the latest active API key for a bot
   *
   * @param botId - ID of the bot
   * @returns Latest active API key, or an error object with an HTTP-like status (404 when no keys exist)
   */
  async getLatestApiKey(botId: string): Promise<any | { error: string; status: number }> {
    try {
      const response = await ApiService.get(`/bot/apikeys/${botId}/latest`);
      return response.data.key;
    } catch (error: any) {
      console.error('[getLatestApiKey] Error:', error?.message || error);
      // Return 404 status if no keys exist
      if (error?.response?.status === 404) {
        return {
          error: 'No active API keys found',
          status: 404
        };
      }
      return {
        error: error?.response?.data?.message || 'Failed to retrieve latest API key',
        status: error?.response?.status || 400
      };
    }
  },

  /**
   * Creates a new API key while deactivating the most recently created active key
   *
   * @param uniqueBotId - ID of the bot
   * @returns Newly created API key or error object
   */
  async regenerateApiKey(uniqueBotId: string): Promise<any | { error: string; status: number }> {
    try {
      // Call the backend endpoint directly which handles the regeneration process
      const response = await ApiService.post(`/bot/apikeys/regenerate`, { uniqueBotId });
      return response.data.key;
    } catch (error: any) {
      console.error('[regenerateApiKey] Error:', error?.message || error);
      return {
        error: error?.response?.data?.message || 'Failed to regenerate API key',
        status: error?.response?.status || 400
      };
    }
  },

  async getNlp({ botId, sensitive }: {
    /** ID of the bot */
    botId: string;
    /** When true, include sensitive NLP configuration in the response */
    sensitive?: boolean;
  }): Promise<any> {
    const LOG_PREFIX = `[getNlp]`;
    const query = sensitive === true ? '?sensitive=true' : '';
    try {
      const response = await ApiService.get(`/schaltzentrale/nlp/${botId}/management${query}`);
      return response.data || [];
    } catch (error: any) {
      console.error(`${LOG_PREFIX} Error when fetching NLP(s). Error: `, error?.message || error);
      return [];
    }
  },
  async saveNlp({ botId, nlp }: {
    /** ID of the bot */
    botId: string;
    /** The NLP management configuration to persist */
    nlp: any;
  }): Promise<{ success: boolean; data?: any; error?: any }> {
    const LOG_PREFIX = `[getNlp]`;

    const data = { nlp };
    // data.autoConfiguration = false
    // In case you want to replace it entirely (not including RegExp). Without missed fields generation

    try {
      const response = await ApiService.put(`/schaltzentrale/nlp/${botId}/management`, data);
      return {
        success: response.data?.success === true,
        data: response.data?.data
      };
    } catch (error: any) {
      const errorData = error?.response?.data?.error || error?.response?.data || error;
      return { success: false, error: errorData };
    }
  },
};

export default BotService;

export { BotService };
